/**
 * Sticky Note Service
 * 포스트잇 관련 API 서비스
 */

import { api } from "./api";
import type {
  StickyNote,
  StickyNoteCreateRequest,
  StickyNoteUpdateRequest,
  StickyNotePositionUpdateRequest,
  StickyNoteShareRequest,
  StickyNoteArchiveRequest,
  StickyNoteListResponse,
  StickyNoteBoard,
  StickyNoteBoardCreateRequest,
  StickyNoteBoardUpdateRequest,
  StickyNoteBoardAddMembersRequest,
  StickyNoteLikeResponse,
  StickyNoteComment,
} from "@/types/stickyNote";

const stickyNoteService = {
  // ─── Board APIs ────────────────────────────────────────────────────────────

  /**
   * 내 팀 보드 목록 조회
   */
  async getBoards(): Promise<StickyNoteBoard[]> {
    const response = await api.get<StickyNoteBoard[]>("/sticky-notes/boards");
    return response.data;
  },

  /**
   * 팀 보드 생성
   */
  async createBoard(data: StickyNoteBoardCreateRequest): Promise<StickyNoteBoard> {
    const response = await api.post<StickyNoteBoard>("/sticky-notes/boards", data);
    return response.data;
  },

  /**
   * 팀 보드 상세 조회
   */
  async getBoard(boardId: number): Promise<StickyNoteBoard> {
    const response = await api.get<StickyNoteBoard>(`/sticky-notes/boards/${boardId}`);
    return response.data;
  },

  /**
   * 팀 보드 이름 변경
   */
  async updateBoard(boardId: number, data: StickyNoteBoardUpdateRequest): Promise<StickyNoteBoard> {
    const response = await api.patch<StickyNoteBoard>(`/sticky-notes/boards/${boardId}`, data);
    return response.data;
  },

  /**
   * 팀 보드 삭제
   */
  async deleteBoard(boardId: number): Promise<void> {
    await api.delete(`/sticky-notes/boards/${boardId}`);
  },

  /**
   * 팀 보드 멤버 추가
   */
  async addBoardMembers(boardId: number, data: StickyNoteBoardAddMembersRequest): Promise<StickyNoteBoard> {
    const response = await api.post<StickyNoteBoard>(`/sticky-notes/boards/${boardId}/members`, data);
    return response.data;
  },

  /**
   * 팀 보드 멤버 제거
   */
  async removeBoardMember(boardId: number, userId: string): Promise<void> {
    await api.delete(`/sticky-notes/boards/${boardId}/members/${userId}`);
  },

  // ─── Note APIs ─────────────────────────────────────────────────────────────

  /**
   * 포스트잇 목록 조회
   */
  async getStickyNotes(params?: {
    visibility?: string;
    board_id?: number;
    include_archived?: boolean;
    page?: number;
    size?: number;
  }): Promise<StickyNoteListResponse> {
    const response = await api.get<StickyNoteListResponse>("/sticky-notes", {
      params,
    });
    return response.data;
  },

  /**
   * 포스트잇 단일 조회
   */
  async getStickyNote(noteId: number): Promise<StickyNote> {
    const response = await api.get<StickyNote>(`/sticky-notes/${noteId}`);
    return response.data;
  },

  /**
   * 포스트잇 생성
   */
  async createStickyNote(data: StickyNoteCreateRequest): Promise<StickyNote> {
    const response = await api.post<StickyNote>("/sticky-notes", data);
    return response.data;
  },

  /**
   * 포스트잇 수정
   */
  async updateStickyNote(
    noteId: number,
    data: StickyNoteUpdateRequest
  ): Promise<StickyNote> {
    const response = await api.patch<StickyNote>(
      `/sticky-notes/${noteId}`,
      data
    );
    return response.data;
  },

  /**
   * 포스트잇 위치/크기 업데이트
   */
  async updateStickyNotePosition(
    noteId: number,
    data: StickyNotePositionUpdateRequest
  ): Promise<StickyNote> {
    const response = await api.patch<StickyNote>(
      `/sticky-notes/${noteId}/position`,
      data
    );
    return response.data;
  },

  /**
   * 포스트잇 z-index 정규화 (순서 유지하면서 1,2,3... 재할당)
   */
  async normalizeZIndex(boardId?: number): Promise<{ normalized_count: number; max_z_index: number }> {
    const response = await api.post<{ normalized_count: number; max_z_index: number }>(
      '/sticky-notes/normalize-z-index',
      null,
      { params: boardId != null ? { board_id: boardId } : undefined }
    );
    return response.data;
  },

  /**
   * 포스트잇 공유 설정
   */
  async shareStickyNote(
    noteId: number,
    data: StickyNoteShareRequest
  ): Promise<StickyNote> {
    const response = await api.patch<StickyNote>(
      `/sticky-notes/${noteId}/share`,
      data
    );
    return response.data;
  },

  /**
   * 포스트잇 고정 토글
   */
  async togglePinStickyNote(noteId: number): Promise<StickyNote> {
    const response = await api.post<StickyNote>(
      `/sticky-notes/${noteId}/toggle-pin`
    );
    return response.data;
  },

  /**
   * 포스트잇 팀에 공유
   */
  async shareToTeam(noteId: number): Promise<StickyNote> {
    const response = await api.post<StickyNote>(
      `/sticky-notes/${noteId}/share-to-team`
    );
    return response.data;
  },

  /**
   * 포스트잇 보관
   */
  async archiveStickyNote(
    noteId: number,
    data: StickyNoteArchiveRequest
  ): Promise<StickyNote> {
    const response = await api.patch<StickyNote>(
      `/sticky-notes/${noteId}/archive`,
      data
    );
    return response.data;
  },

  /**
   * 포스트잇 삭제
   */
  async deleteStickyNote(noteId: number): Promise<void> {
    await api.delete(`/sticky-notes/${noteId}`);
  },

  /**
   * 포스트잇 이미지 업로드
   */
  async uploadImage(noteId: number, file: File): Promise<StickyNote> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post<StickyNote>(
      `/sticky-notes/${noteId}/image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * 포스트잇 이미지 삭제
   */
  async deleteImage(noteId: number): Promise<StickyNote> {
    const response = await api.delete<StickyNote>(`/sticky-notes/${noteId}/image`);
    return response.data;
  },

  /**
   * 미확인 포스트잇 개수 조회
   * @param boardId 팀 보드 ID (undefined면 공유 보드)
   */
  async getUnreadCount(boardId?: number): Promise<{ count: number; last_visit: string | null }> {
    const response = await api.get<{ count: number; last_visit: string | null }>("/sticky-notes/unread/count", {
      params: boardId !== undefined ? { board_id: boardId } : undefined,
    });
    return response.data;
  },

  /**
   * 보드 방문 처리
   * @param boardId 팀 보드 ID (undefined면 공유 보드)
   */
  async markBoardVisited(boardId?: number): Promise<void> {
    await api.post("/sticky-notes/mark-visited", null, {
      params: boardId !== undefined ? { board_id: boardId } : undefined,
    });
  },

  // ─── Like / Comment APIs ───────────────────────────────────────────────────

  /**
   * 좋아요 토글
   */
  async toggleLike(noteId: number): Promise<StickyNoteLikeResponse> {
    const response = await api.post<StickyNoteLikeResponse>(`/sticky-notes/${noteId}/like`);
    return response.data;
  },

  /**
   * 댓글 목록 조회
   */
  async getComments(noteId: number): Promise<StickyNoteComment[]> {
    const response = await api.get<StickyNoteComment[]>(`/sticky-notes/${noteId}/comments`);
    return response.data;
  },

  /**
   * 댓글 생성
   */
  async createComment(noteId: number, content: string): Promise<StickyNoteComment> {
    const response = await api.post<StickyNoteComment>(`/sticky-notes/${noteId}/comments`, { content });
    return response.data;
  },

  /**
   * 댓글 수정
   */
  async updateComment(noteId: number, commentId: number, content: string): Promise<StickyNoteComment> {
    const response = await api.patch<StickyNoteComment>(
      `/sticky-notes/${noteId}/comments/${commentId}`,
      { content }
    );
    return response.data;
  },

  /**
   * 댓글 삭제
   */
  async deleteComment(noteId: number, commentId: number): Promise<void> {
    await api.delete(`/sticky-notes/${noteId}/comments/${commentId}`);
  },
};

export default stickyNoteService;
